/**
 * localStorage access that survives every environment where
 * `window.localStorage` is not a usable `Storage` object:
 *
 *  - **SSR** — there is no `window` at all.
 *  - **Android WebViews with DOM storage disabled** — `window.localStorage` is
 *    literally `null`, so a bare `localStorage.getItem(key)` throws
 *    `TypeError: Cannot read properties of null (reading 'getItem')`. A
 *    `typeof window === 'undefined'` guard does *not* cover this; it was the
 *    live crash behind Sentry TESLA-LOCK-SOUND-52/53 (TES-1872).
 *  - **Safari private mode / quota exceeded** — the property access or the
 *    `setItem` call itself throws.
 *
 * Every helper degrades to a no-op instead of throwing. Callers that need to
 * know whether the write landed can check the boolean return.
 */

function getStore(): Storage | null {
  if (typeof window === 'undefined') return null
  try {
    // Property access alone can throw when cookies/storage are blocked, and can
    // return null in restricted WebViews — hence both the try and the ?? null.
    return window.localStorage ?? null
  } catch {
    return null
  }
}

/** Returns the stored string, or null when absent or storage is unavailable. */
export function readStorage(key: string): string | null {
  try {
    return getStore()?.getItem(key) ?? null
  } catch {
    return null
  }
}

/** Writes a value. Returns false when storage is unavailable or full. */
export function writeStorage(key: string, value: string): boolean {
  try {
    const store = getStore()
    if (!store) return false
    store.setItem(key, value)
    return true
  } catch {
    return false
  }
}

/** Removes a key. Returns false when storage is unavailable. */
export function removeStorage(key: string): boolean {
  try {
    const store = getStore()
    if (!store) return false
    store.removeItem(key)
    return true
  } catch {
    return false
  }
}

/** True when localStorage can actually be read on this client. */
export function isStorageAvailable(): boolean {
  return getStore() !== null
}

/**
 * A snapshot of every key currently in storage, or `[]` when unavailable.
 *
 * Snapshotting matters: callers scan for prefixed keys and `removeStorage()`
 * the stale ones, and mutating during a live `store.key(i)` walk shifts the
 * indices underneath the loop and silently skips entries.
 */
export function storageKeys(): string[] {
  try {
    const store = getStore()
    if (!store) return []
    const keys: string[] = []
    for (let i = 0; i < store.length; i++) {
      const key = store.key(i)
      if (key !== null) keys.push(key)
    }
    return keys
  } catch {
    return []
  }
}
